import React, { useEffect, useState } from "react";
import {
  fetchPosts,
  fetchCategories,
  fetchPostsByCategory,
  searchPosts, 
} from "../../services/api";
import {decodeHTML} from "../../utils.js"

import styles from "../Blogs/blogs.module.css";
import Animations from "../../components/Animations/animations";
import { Link } from "react-router-dom";

const Blogs = () => {


  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPosts, setTotalPosts] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryPosts, setCategoryPosts] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [searchQuery, setSearchQuery] = useState(""); // Search query state
  const [searchResults, setSearchResults] = useState([]); // Search results state
  const [searchLoading, setSearchLoading] = useState(false); // Search loading state
  const [debounceTimeout, setDebounceTimeout] = useState(null); // Timeout for debouncing
  const [noInputMessage, setNoInputMessage] = useState(""); // Message for empty input

  // Load categories
  const loadCategories = async () => {
    setCategoriesLoading(true);
    try {
      const categoriesData = await fetchCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Load posts
  const loadPosts = async (pageNumber) => {
    setLoading(true);
    try {
      const { posts: postsData, totalPosts: total } = await fetchPosts(
        pageNumber
      );
      setTotalPosts(total);
      if (postsData.length > 0) {
        setPosts((prevPosts) => [...prevPosts, ...postsData]);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
    }
    setLoading(false);
  };

  // Load posts by category
  const loadPostsByCategory = async (categoryId) => {
    setLoadingPosts(true);
    try {
      const { posts: categoryPostsData } = await fetchPostsByCategory(
        categoryId,
        1
      );
      setCategoryPosts(categoryPostsData);
    } catch (error) {
      console.error("Error fetching posts by category:", error);
    } finally {
      setLoadingPosts(false);
    }
  };

  // Search posts by title (using the search API)

  const searchPostsByTitle = async (query) => {
    if (!query.trim()) {
      setNoInputMessage("Please type a keyword to search.");
      return;
    }

    setNoInputMessage(""); // Clear the message
    setSearchLoading(true);

    try {
      // Assuming `searchPosts` is a function to search posts by title
      const searchData = await searchPosts(query);
      const filteredResults = searchData.posts.filter((post) =>
        post.title.rendered.toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(filteredResults); // Set filtered posts
    } catch (error) {
      console.error("Error searching posts:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle search input change with debouncing
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value.trimStart()); // Trim leading spaces as user types
  
    // Clear previous timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
  
    // Reset search results immediately
    setSearchResults([]);
  
    // Set a new timeout to trigger the search after 500ms
    setDebounceTimeout(
      setTimeout(() => {
        searchPostsByTitle(value);
      }, 500)
    );
  };
  

  useEffect(() => {
    loadPosts(page);
    loadCategories();
  }, [page]);

  useEffect(() => {
    if (selectedCategory) {
      loadPostsByCategory(selectedCategory.id);
    }
  }, [selectedCategory]);

  const loadMorePosts = () => {
    setPage((prevPage) => prevPage + 1);
  };

  const CategoryPostsLayout = ({ posts }) => {
    return (
      <div className={styles.categoryPostsLayout}>
        <div className={styles.categoriesLeftColumn}>
          {loadingPosts ? (
            <div className={styles.allArticlesCard}>
              <div
                className={`${styles.allArticles__cardImg} skeleton`}
                style={{ height: "150px" }}
              ></div>
              <div className={styles.blogMeta}>
                <h6
                  className="skeleton"
                  style={{ width: "80%", height: "20px" }}
                ></h6>
                <div className={styles.blogAuthor}>
                  <div
                    className="skeleton"
                    style={{ width: "22px", height: "22px" }}
                  ></div>
                  <div>
                    <span
                      className="skeleton"
                      style={{ width: "100px", height: "16px" }}
                    ></span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            posts[0] && (
              <Link to={`/blog/${posts[0].slug}`}>
              <div className={styles.allArticlesCard}>
                <div className={styles.allArticles__cardImg}>
                  <img
                    src={
                      posts[0]._embedded?.["wp:featuredmedia"]?.[0]
                        ?.source_url || "assets/placeholder-image.webp"
                    }
                    alt={decodeHTML(posts[0].title.rendered || "Post Image")}
                    width="100%"
                    height="100%"
                    loading="lazy"
                  />
                </div>
                <div className={styles.blogMeta}>
                  <h6>{decodeHTML(posts[0].title.rendered)}</h6>
                  <div className={styles.blogAuthor}>
                    <div>
                      <img
                        src="assets/user.webp"
                        width="22"
                        height="22"
                        alt="Blog Author"
                      />
                    </div>
                    <div>
                      <span>
                        {posts[0]._embedded?.author?.[0]?.name ||
                          "Unknown Author"}
                      </span>
                      <span> . </span>
                      <span>
                        {new Date(
                          posts[0].modified || posts[0].date
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              </Link>
            )
          )}
        </div>

        <div className={styles.categoriesRightColumn}>
          {loadingPosts
            ? Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className={styles.allArticlesCard}>
                  <div
                    className={`${styles.allArticles__cardImg} skeleton`}
                    style={{ height: "150px" }}
                  ></div>
                  <div className={styles.blogMeta}>
                    <h6
                      className="skeleton"
                      style={{ width: "80%", height: "20px" }}
                    ></h6>
                    <div className={styles.blogAuthor}>
                      <div
                        className="skeleton"
                        style={{ width: "22px", height: "22px" }}
                      ></div>
                      <div>
                        <span
                          className="skeleton"
                          style={{ width: "100px", height: "16px" }}
                        ></span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            : posts.slice(1, 4).map((post) => (
              <Link to={`/blog/${post.slug}`}>
                <div key={post.id} className={styles.allArticlesCard}>
                  <div className={styles.allArticles__cardImg}>
                    <img
                      src={
                        post._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
                        "assets/placeholder-image.webp"
                      }
                      alt={decodeHTML(post.title.rendered || "Blog Image")}
                      width="100%"
                      height="100%"
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.blogMeta}>
                    <h6>{decodeHTML(post.title.rendered)}</h6>
                    <div className={styles.blogAuthor}>
                      <div>
                        <img
                          src="assets/user.webp"
                          width="22"
                          height="22"
                          alt="Blog Author"
                        />
                      </div>
                      <div>
                        <span>
                          {post._embedded?.author?.[0]?.name ||
                            "Unknown Author"}
                        </span>
                        <span> . </span>
                        <span>
                          {new Date(
                            post.modified || post.date
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                </Link>
              ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <section className={styles.blogsBanner}>
        <div className={styles.container}>
          <Animations animationType="fadeInUp" threshold={0.2}>
            <h1>Stay Informed and Inspired
            </h1>

          
              <div className={styles.blogSearch}>
                <div className={styles.blogSearchField}>
                <div>
                <svg viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M15.97 17.5886C14.491 18.8266 12.586 19.5736 10.509 19.5736C5.812 19.5736 2 15.7616 2 11.0656C2 6.36962 5.812 2.55762 10.509 2.55762C15.204 2.55762 19.017 6.36962 19.017 11.0656C19.017 13.1436 18.27 15.0496 17.032 16.5266L21.781 21.2766C21.927 21.4226 22 21.6146 22 21.8076C22 22.3946 21.463 22.5576 21.25 22.5576C21.058 22.5576 20.866 22.4846 20.719 22.3376L15.97 17.5886ZM10.509 4.05862C6.641 4.05862 3.502 7.19862 3.502 11.0656C3.502 14.9326 6.641 18.0726 10.509 18.0726C14.375 18.0726 17.516 14.9326 17.516 11.0656C17.516 7.19862 14.375 4.05862 10.509 4.05862Z" fill="#64748B"/>
</svg>

                    </div>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault(); // Prevent form submission (page reload)
                      searchPostsByTitle(searchQuery); // Trigger the search
                    }}
                  >
                   
                    <input
                      type="text"
                      placeholder="Search blog or article..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      pattern="\S.*"
                      className={styles.searchInput}
                    />
                    <input type="submit" id="searchsubmit" value="Search" />
                  </form>
                  {/* Display No Input Message */}
                  {noInputMessage && !searchQuery && <p className={styles.noResultsMessage}>{noInputMessage}</p>}


                  {searchQuery && searchResults.length > 0 ? (
                    <ul className={styles.searchResults}>
                      {searchResults.map((result) => (
                        <li key={result.id}>
                          <Link to={`/blog/${result.slug}`}>
                            {decodeHTML(result.title.rendered)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    !searchLoading &&
                    searchQuery &&
                    searchResults.length === 0 && (
                      <p className={styles.noResultsMessage}>
                        No results found
                      </p>
                    )
                  )}
                </div>
              </div>
         
          </Animations>
        </div>
      </section>
      <section className={styles.articlesTabs}>
        <div className={styles.container}>
          <h2>Recent Article</h2>
          <p className={styles.subText}>
            Access thousands of personalized, expert-curated articles
            effortlessly
          </p>
          {/* Tab Section for Categories */}
          <div className={styles.categoriesTabs}>
            <ul className={styles.categoriesTabList}>
              {/* All Posts Tab */}
              <li
                className={selectedCategory === null ? styles.active : ""}
                onClick={() => setSelectedCategory(null)}
              >
                All
              </li>

              {/* Show Skeleton for Categories While Loading */}
              {categoriesLoading
                ? // Loop through a fixed number of skeletons (e.g., 6 categories) while loading
                  Array.from({ length: 6 }).map((_, index) => (
                    <li key={index} className={styles.skeletonCategory}></li>
                  ))
                : // Display categories when they are loaded
                  categories.map((category) => (
                    <li
                      key={category.id}
                      className={
                        selectedCategory?.id === category.id
                          ? styles.active
                          : ""
                      }
                      onClick={() => setSelectedCategory(category)}
                    >
                      {category.name}
                    </li>
                  ))}
            </ul>

            {/* Tab Content */}

            <div className={styles.categoriesTabContent}>
              {loading ? (
                // Skeleton placeholder for loading state
                <div className={styles.tabContentSkeleton}>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div
                      key={index}
                      className={`${styles.skeletonCard} skeleton`}
                    ></div>
                  ))}
                </div>
              ) : (
                // Render posts for the selected category or all posts
                <>
                  {selectedCategory === null ? (
                    <CategoryPostsLayout posts={posts.slice(0, 4)} />
                  ) : (
                    <CategoryPostsLayout posts={categoryPosts.slice(0, 4)} />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
      <section className={styles.allArticles}>
        <div className={styles.container}>
          <h2>Recommended Article</h2>
          <p className={styles.subText}>
            Explore Thousands of Expertly Curated Articles with Our Powerful
            Algorithm
          </p>

          <div className={styles.allArticlesWrapper}>
            {loading && posts.length === 0
              ? Array.from({ length: 9 }).map((_, index) => (
                  // Skeleton placeholder
                  <div key={index} className={styles.allArticlesCard}>
                    <div
                      className={`${styles.allArticles__cardImg} skeleton`}
                      style={{ height: "200px" }}
                    ></div>
                    <div className={styles.blogMeta}>
                      <h6
                        className="skeleton"
                        style={{
                          width: "80%",
                          height: "20px",
                          marginBottom: "8px",
                        }}
                      ></h6>
                      <div
                        className="skeleton"
                        style={{ width: "60%", height: "16px" }}
                      ></div>
                    </div>
                  </div>
                ))
              : posts.map((post) => (
                <Link to={`/blog/${post.slug}`}>
                  <div key={post.id} className={styles.allArticlesCard}>
                    {/* Featured Image */}
                    <div className={styles.allArticles__cardImg}>
                      <img
                        src={
                          post._embedded?.["wp:featuredmedia"]?.[0]
                            ?.source_url || "assets/placeholder-image.webp"
                        }
                        alt={post.title.rendered || "Blog Image"}
                        width="100%"
                        height="100%"
                        loading="lazy"
                      />
                    </div>

                    {/* Blog Meta */}
                    <div className={styles.blogMeta}>
                      {/* Blog Title */}
                      <h6>{post.title.rendered}</h6>

                      {/* Author and Date */}
                      <div className={styles.blogAuthor}>
                        <div>
                          <img
                            src="assets/user.webp"
                            width="22"
                            height="22"
                            alt="Blog Author"
                          />
                        </div>
                        <div>
                          <span>
                            {post._embedded?.author?.[0]?.name ||
                              "Unknown Author"}
                          </span>
                          <span> . </span>
                          <span>
                            {new Date(
                              post.modified || post.date
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  </Link>
                ))}
          </div>

          <div className={styles.allArticlesWrapper}>
            {loading &&
              page > 1 &&
              hasMore &&
              Array.from({ length: 9 }).map((_, index) => (
                // Skeleton placeholder
                <div className={styles.allArticlesCardSkeleton}>
                <div key={index} className={styles.allArticlesCard}>
                  <div
                    className={`${styles.allArticles__cardImg} skeleton`}
                    style={{ height: "200px" }}
                  ></div>
                  <div className={styles.blogMeta}>
                    <h6
                      className="skeleton"
                      style={{
                        width: "80%",
                        height: "20px",
                        marginBottom: "8px",
                      }}
                    ></h6>
                    <div
                      className="skeleton"
                      style={{ width: "60%", height: "16px" }}
                    ></div>
                  </div>
                </div>
                </div>
              ))}
          </div>
          {/* Load More Button */}
          {hasMore && !loading && posts.length < totalPosts && (
            <button className={styles.loadMoreButton} onClick={loadMorePosts}>
              Load More
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blogs;
